/**
 * Status badge component
 * Displays job or cell status with appropriate styling
 */

import { Tag, Badge, Tooltip } from 'antd';
import {
  ClockCircleOutlined,
  SyncOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  StopOutlined,
  PauseCircleOutlined,
  MinusCircleOutlined,
} from '@ant-design/icons';
import type { JobStatus } from '@/types/job';
import type { GridCellStatus } from '@/types/grid';
import { JOB_STATUS_CONFIG, CELL_STATUS_CONFIG } from '@/utils/constants';

interface StatusBadgeProps {
  status: JobStatus | GridCellStatus;
  type?: 'job' | 'cell';
  size?: 'small' | 'default' | 'large';
  showIcon?: boolean;
  showLabel?: boolean;
  tooltip?: string;
}

const jobIcons: Record<JobStatus, React.ReactNode> = {
  pending: <ClockCircleOutlined />,
  running: <SyncOutlined spin />,
  paused: <PauseCircleOutlined />,
  completed: <CheckCircleOutlined />,
  failed: <CloseCircleOutlined />,
  cancelled: <StopOutlined />,
};

const cellIcons: Record<GridCellStatus, React.ReactNode> = {
  pending: <ClockCircleOutlined />,
  queued: <ClockCircleOutlined />,
  processing: <SyncOutlined spin />,
  completed: <CheckCircleOutlined />,
  failed: <CloseCircleOutlined />,
  skipped: <MinusCircleOutlined />,
};

export function StatusBadge({
  status,
  type = 'job',
  size = 'default',
  showIcon = true,
  showLabel = true,
  tooltip,
}: StatusBadgeProps) {
  const isJob = type === 'job';
  const config = isJob
    ? JOB_STATUS_CONFIG[status as JobStatus]
    : CELL_STATUS_CONFIG[status as GridCellStatus];

  const icon = isJob
    ? jobIcons[status as JobStatus]
    : cellIcons[status as GridCellStatus];

  const fontSize = size === 'small' ? 12 : size === 'large' ? 14 : 13;
  const padding = size === 'small' ? '0 6px' : size === 'large' ? '4px 12px' : '2px 8px';

  const tag = (
    <Tag
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        fontSize,
        padding,
        margin: 0,
        backgroundColor: config.bgColor,
        color: config.color,
        border: `1px solid ${config.borderColor}`,
        borderRadius: 4,
      }}
      role="status"
      aria-label={`Status: ${config.label}`}
    >
      {showIcon && <span aria-hidden="true">{icon}</span>}
      {showLabel && <span>{config.label}</span>}
    </Tag>
  );

  if (tooltip) {
    return <Tooltip title={tooltip}>{tag}</Tooltip>;
  }

  return tag;
}

/**
 * Job status badge
 */
export function JobStatusBadge({
  status,
  ...props
}: Omit<StatusBadgeProps, 'status' | 'type'> & { status: JobStatus }) {
  return <StatusBadge status={status} type="job" {...props} />;
}

/**
 * Cell status badge
 */
export function CellStatusBadge({
  status,
  ...props
}: Omit<StatusBadgeProps, 'status' | 'type'> & { status: GridCellStatus }) {
  return <StatusBadge status={status} type="cell" {...props} />;
}

/**
 * Status dot (minimal indicator)
 */
export function StatusDot({
  status,
  type = 'job',
  tooltip,
}: {
  status: JobStatus | GridCellStatus;
  type?: 'job' | 'cell';
  tooltip?: string;
}) {
  const isJob = type === 'job';
  const config = isJob
    ? JOB_STATUS_CONFIG[status as JobStatus]
    : CELL_STATUS_CONFIG[status as GridCellStatus];

  const isPulsing = status === 'running' || status === 'processing';

  const dot = (
    <Badge
      status={isPulsing ? 'processing' : 'default'}
      color={config.color}
      style={{ cursor: tooltip ? 'help' : 'default' }}
    />
  );

  if (tooltip) {
    return <Tooltip title={tooltip}>{dot}</Tooltip>;
  }

  return dot;
}

/**
 * Inline status with text
 */
export function InlineStatus({
  status,
  type = 'job',
  prefix,
  suffix,
}: {
  status: JobStatus | GridCellStatus;
  type?: 'job' | 'cell';
  prefix?: string;
  suffix?: string;
}) {
  const isJob = type === 'job';
  const config = isJob
    ? JOB_STATUS_CONFIG[status as JobStatus]
    : CELL_STATUS_CONFIG[status as GridCellStatus];

  const icon = isJob
    ? jobIcons[status as JobStatus]
    : cellIcons[status as GridCellStatus];

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        color: config.color,
      }}
    >
      {prefix && <span style={{ color: 'var(--color-text-secondary)' }}>{prefix}</span>}
      {icon}
      <span style={{ fontWeight: 500 }}>{config.label}</span>
      {suffix && <span style={{ color: 'var(--color-text-secondary)' }}>{suffix}</span>}
    </span>
  );
}

export default StatusBadge;
